import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react';
import {useHistory} from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import {Button, Grid} from '@customink/pigment-react';
import Pagination from '@mui/material/Pagination';
import {useAppSelector} from '../../hooks';
import UserDesigns from '../UserDesigns';
import {useLazyGetDesignQuery} from '../../api/designsApi';
import {
  useLazyFetchProductsQuery,
  useFetchSinglesProductIdsQuery
} from '../../api/productsApi';
import {
  useCreateStoreProductMutation,
  StoreProductParamsType,
  useGetStoreDetailsQuery
} from '../../api/storesApi';
import ProductCard from '../ProductCard';
import ProductColorChooser from '../ProductColorChooser';
import XCloseIcon from '../../assets/icons/x_close.svg';
import './index.scss';

type NewStoreProductProps = {
  storeSlug: string;
};

const NewStoreProduct: React.FC<NewStoreProductProps> = ({
  storeSlug
}: NewStoreProductProps): ReactElement => {
  useGetStoreDetailsQuery(storeSlug);
  const [step, setStep] = useState(1);
  const [selectedDesignCid, setDesign] = useState('');
  const [selectedProductId, setProduct] = useState('');
  const [selectedProductName, setProductName] = useState('');
  const [selectedProductColorIds, setProductColors] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const history = useHistory();
  const {
    data: singlesProductIdResults,
    isSuccess,
    isError,
    isLoading
  } = useFetchSinglesProductIdsQuery({page});
  const [triggerFetchProductsQuery, productResults] =
    useLazyFetchProductsQuery();
  const [
    triggerCreateStoreProductMutation,
    {isSuccess: isCreateStoreProductSuccess}
  ] = useCreateStoreProductMutation();
  const [triggerGetDesignQuery, designResult] = useLazyGetDesignQuery();

  const handleCreateStoreProductSuccess = () => {
    if (isCreateStoreProductSuccess) {
      history.push(`/${storeSlug}/manage/products`);
    }
  };
  useEffect(handleCreateStoreProductSuccess, [isCreateStoreProductSuccess]);
  useEffect(() => {
    triggerFetchProductsQuery({
      ids: singlesProductIdResults?.results || []
    });
  }, [singlesProductIdResults]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const fetchProducts = () => {
    triggerGetDesignQuery(selectedDesignCid);
  };

  const selectProduct = useCallback(
    product => {
      setProduct(String(product.id));
      setProductName(product.name);
    },
    [setProduct, setProductName]
  );

  const selectColors = useCallback(
    colors => {
      setProductColors(colors);
    },
    [setProductColors]
  );

  const handleClose = useCallback(() => {
    history.push(`/${storeSlug}/manage/products`);
  }, [history]);

  const colorsForProduct = (productId: string) => {
    return productResults.data
      .find(p => String(p.id) === productId)
      .colors.filter(c => c.singles_enabled);
  };

  const createProduct = () => {
    const storeProductParams: StoreProductParamsType = {
      url_slug: storeSlug,
      product: {
        name: selectedProductName,
        style_id: Number(selectedProductId),
        design_composite_id: selectedDesignCid,
        enabled: true,
        variants: selectedProductColorIds.map(c => {
          return {color_id: Number(c)};
        })
      }
    };
    triggerCreateStoreProductMutation(storeProductParams);
  };

  const shouldShowSpinner = isLoading;

  const navigationContainer = useMemo(() => {
    const handlePageChange = (event: React.ChangeEvent, value: number) => {
      setPage(value);
    };

    return (
      <div>
        {(isSuccess || isError) &&
          singlesProductIdResults?.results &&
          singlesProductIdResults?.total_count > 42 && (
            <Pagination
              count={singlesProductIdResults.total_pages}
              defaultPage={page}
              onChange={handlePageChange}
              size="large"
              sx={{
                marginTop: '2rem'
              }}
            />
          )}
      </div>
    );
  }, [page, setPage, singlesProductIdResults]);

  const renderStepChangeBar = (
    stepText: string,
    disabled: boolean,
    onClick: () => void = () => {
      /* none */
    }
  ) => {
    return (
      <div className="stepChangeBar">
        <div className="actions">
          {step !== 1 && (
            <Button
              size="large"
              variant="link"
              className="stepBackButton"
              onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
          <Button
            size="large"
            className="stepButton"
            disabled={disabled}
            onClick={() => {
              setStep(step + 1);
              onClick();
            }}>
            {stepText}
          </Button>
        </div>
      </div>
    );
  };

  const renderCloseButton = () => {
    return (
      <Button
        className="closeButton"
        size="large"
        variant="link"
        onClick={handleClose}>
        <img src={XCloseIcon} alt="Cancel" />
      </Button>
    );
  };

  const renderStep1 = () => {
    return (
      <div className="step1">
        {storeDetails && (
          <UserDesigns
            email={storeDetails.storeOwnerEmail}
            setDesign={setDesign}
            selectedDesign={selectedDesignCid}
          />
        )}
        {renderStepChangeBar(
          'Pick a product',
          !storeDetails || selectedDesignCid === '',
          fetchProducts
        )}
      </div>
    );
  };

  const renderStep2 = () => {
    return (
      <div className="step2">
        <h1>Next, decide what product you want your design to go on.</h1>
        <p>
          This is the product you want to put your design on. You&#39;ll pick
          the colors in the next step.
        </p>
        {shouldShowSpinner && <CircularProgress className="loadingSpinner" />}
        <Grid
          container
          className="productGrid"
          direction="row"
          spacing={2}
          alignItems="stretch">
          {!shouldShowSpinner &&
            designResult.data &&
            productResults.data &&
            productResults.data.map(product => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <ProductCard
                  product={product}
                  productName={product.name}
                  setProduct={selectProduct}
                  selected={selectedProductId === String(product.id)}
                  design={designResult.data}
                />
              </Grid>
            ))}
        </Grid>
        {!shouldShowSpinner && navigationContainer}
        {renderStepChangeBar('Select your colors', selectedProductId === '')}
      </div>
    );
  };

  const renderStep3 = () => {
    return (
      <div>
        <h1>Finally, decide what colors you want your product to be.</h1>
        <p>You can pick up to six different colors for each item.</p>
        <ProductColorChooser
          colors={colorsForProduct(selectedProductId)}
          selectedColorIds={selectedProductColorIds}
          setSelectedColors={selectColors}
          maxSelectedColors={6}
        />
        <div className="stepChangeBar">
          <div className="actions">
            <Button
              size="large"
              variant="link"
              className="stepBackButton"
              onClick={() => {
                setStep(step - 1);
                selectColors([]);
              }}>
              Back
            </Button>
            <Button
              size="large"
              className="stepButton"
              disabled={selectedProductColorIds.length === 0}
              onClick={createProduct}>
              Add Product to Store
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="newStoreProduct">
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {renderCloseButton()}
    </div>
  );
};

export default NewStoreProduct;
